import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  Divider,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import {
  CustomQualityControlDto,
  GeneratorElutionDto,
  PreparationLotDto,
  QcTestResultDto,
  QCResult,
  QCType,
  VisualQualityControlDto,
  VisualQualityControlType,
  QualityControlStatus,
} from '@services/dtos'
import { useEffect, useState } from 'react'
import { UpTransition } from '../animations'
import { QcResultDetailDialog } from './QcResultDetailDialog'
import { getObservedValue } from '@utils/qualityControl.utils'
import { isNil } from 'lodash'
import { useAppDispatch } from '@hooks/reduxHooks'
import { useSelector } from '@state/store'
import {
  getGeneratorElutionQualityControls,
  getPreparationLotQualityControls,
} from '@state/reducers/operationQualityControlsReducer'
import moment from 'moment'
import { VisualQualityControlTypeLabels } from '@utils/constants'
import DataSaverOnIcon from '@mui/icons-material/DataSaverOn'
import BlockIcon from '@mui/icons-material/Block'
import HistoryIcon from '@mui/icons-material/History'
import {
  RecordQcTestResultDialog,
  ResultChip,
  getRecordTooltip,
} from './RecordQcTestResultDialog'
import { InvalidateQcResultDialog } from './InvalidateQcResultDialog'
import { CompleteQcDialog } from './CompleteQcDialog'
import { getQualityControlExpectedLabel } from '@utils/qualityControl.utils'

function RecordedResult({ result, onInvalidate }: { result: QcTestResultDto; onInvalidate: () => void }) {
  const [detailOpen, setDetailOpen] = useState(false)
  const value = getObservedValue(result)
  return (
    <Box display="flex" flexDirection="column" alignItems="flex-end" gap={0.5}>
      <Box display="flex" alignItems="center" gap={0.5}>
        <Tooltip title="Voir le détail" arrow>
          <span style={{ cursor: 'pointer' }} onClick={() => setDetailOpen(true)}>
            <ResultChip result={result.result} />
          </span>
        </Tooltip>
        <Tooltip title="Invalider ce résultat">
          <IconButton size="small" onClick={onInvalidate} color="default" sx={{ opacity: 0.5, '&:hover': { opacity: 1, color: 'error.main' } }}>
            <BlockIcon sx={{ fontSize: 15 }} />
          </IconButton>
        </Tooltip>
      </Box>
      {value && (
        <Typography variant="caption" fontWeight={500}>
          Obtenu : {value}{result.unit ? ` ${result.unit}` : ''}
        </Typography>
      )}
      <QcResultDetailDialog
        result={result}
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
      />
    </Box>
  )
}

function InvalidationHistory({ qcResults }: { qcResults: QcTestResultDto[] }) {
  if (qcResults.length === 0) {
    return null
  }
  const tooltipContent = qcResults.map((qcResult) => {
    const log = qcResult.invalidationLog
    if (!log) {
      return null
    }
    const value = getObservedValue(qcResult)
    const agent = log.data?.invalidatedBy
    const pressedBy = log.createdBy
    const agentLabel = agent ? `${agent.firstName} ${agent.lastName}` : `${pressedBy.firstName} ${pressedBy.lastName}`
    const pressedByLabel = agent ? ` (saisi par ${pressedBy.firstName} ${pressedBy.lastName}, ${log.log ?? ''})` : ''
    return `${moment(log.createdAt).format('DD/MM/YY HH:mm')} : ${value ? `"${value}${qcResult.unit ? ` ${qcResult.unit}` : ''}"` : ''} invalidé par ${agentLabel} ${pressedByLabel}`
  }).join('\n')
  return (
    <Tooltip title={<span style={{ whiteSpace: 'pre-line' }}>{tooltipContent}</span>} placement="left">
      <Box display="flex" alignItems="center" gap={0.5} sx={{ cursor: 'default', opacity: 0.5 }}>
        <HistoryIcon sx={{ fontSize: 14 }} />
        <Typography variant="caption">{qcResults.length} invalidé{qcResults.length > 1 ? 's' : ''}</Typography>
      </Box>
    </Tooltip>
  )
}

function ControlRow({
  label,
  expectedLabel,
  isRequired,
  activeResult,
  invalidatedResults,
  tooltipLabel,
  onRecord,
  onInvalidate,
}: {
  label: string
  expectedLabel: string
  isRequired: boolean
  activeResult?: QcTestResultDto
  invalidatedResults: QcTestResultDto[]
  tooltipLabel: string
  onRecord: () => void
  onInvalidate: (resultId: string, label: string) => void
}) {
  return (
    <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={1}>
      <Box>
        <Box display="flex" alignItems="center" gap={0.75}>
          <Typography variant="body2">{label}</Typography>
          <Chip
            size="small"
            label={isRequired ? 'Obligatoire' : 'Optionnel'}
            color={isRequired ? 'secondary' : 'default'}
            variant="outlined"
            sx={{ height: 18, fontSize: 10, '& .MuiChip-label': { px: 0.75 } }}
          />
        </Box>
        <Typography variant="caption" color="text.secondary">Attendu : {expectedLabel}</Typography>
      </Box>
      <Box display="flex" flexDirection="column" alignItems="flex-end" gap={0.5}>
        {activeResult ? (
          <RecordedResult result={activeResult} onInvalidate={() => onInvalidate(activeResult.id, label)} />
        ) : (
          <Tooltip title={tooltipLabel}>
            <IconButton size="small" onClick={onRecord} color="primary">
              <DataSaverOnIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        <InvalidationHistory qcResults={invalidatedResults} />
      </Box>
    </Box>
  )
}

type RecordTarget = {
  kind: QCType
  visualQualityControl: VisualQualityControlDto | null
  customQualityControl: CustomQualityControlDto | null
}

type Props =
  | { open: boolean; onClose: () => void; entityType: 'preparationLot'; entity: PreparationLotDto }
  | { open: boolean; onClose: () => void; entityType: 'generatorElution'; entity: GeneratorElutionDto }

export function QualityControlsDialog({ open, onClose, entityType, entity }: Props) {
  const dispatch = useAppDispatch()
  const { assignment, testResults, loading, submitting, canComplete, completionLog } = useSelector(({ operationQualityControls }) => operationQualityControls)
  const [recordTarget, setRecordTarget] = useState<RecordTarget | null>(null)
  const [invalidateTarget, setInvalidateTarget] = useState<{ resultId: string; controlLabel: string; resultLabel: string } | null>(null)
  const [completeOpen, setCompleteOpen] = useState(false)

  const handleInvalidate = (resultId: string, controlLabel: string) => {
    const result = testResults.find((r) => r.id === resultId)
    const resultLabel = result?.result === QCResult.PASS ? 'Conforme' : 'Non conforme'
    setInvalidateTarget({ resultId, controlLabel, resultLabel })
  }

  useEffect(() => {
    if (!open) {
      return
    }
    if (entityType === 'preparationLot') {
      dispatch(getPreparationLotQualityControls(entity.id))
    }
    if (entityType === 'generatorElution') {
      dispatch(getGeneratorElutionQualityControls(entity.id))
    }
  }, [open, entityType, entity.id, dispatch])

  const getSubTitle = (): string => {
    if (entityType === 'preparationLot') {
      const lot = entity as PreparationLotDto
      return `${lot.compound.name} - ${moment(lot.preparedAt).format('DD/MM/YYYY HH:mm')}`
    }
    if (entityType === 'generatorElution') {
      const elution = entity as GeneratorElutionDto
      return `${elution.isotope.name} - ${moment(elution.createdAt).format('DD/MM/YYYY HH:mm')}`
    }
    return ''
  }

  const getActiveResult = (predicate: (r: QcTestResultDto) => boolean) =>
    testResults.find((r) => !r.invalidationLog && predicate(r))
  const getInvalidatedResults = (predicate: (r: QcTestResultDto) => boolean) =>
    testResults.filter((r) => !!r.invalidationLog && predicate(r))

  const hasContent = assignment && (
    assignment.customQualityControls.length > 0 ||
    assignment.visualQualityControls.length > 0 ||
    !isNil(assignment.radiochemicalPurityPercent) ||
    !isNil(assignment.radionuclidicPurityPercent) ||
    !isNil(assignment.phMinimum) ||
    !isNil(assignment.phMaximum)
  )

  const handleRecord = (target: RecordTarget) => setRecordTarget(target)

  const completedBy = completionLog?.data?.completedBy ?? completionLog?.createdBy ?? null
  const completedByLabel = completedBy ? `${completedBy.firstName} ${completedBy.lastName}` : null

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth TransitionComponent={UpTransition}>
        <DialogContent className="p-0">
          <div className="flex flex-col gap-4 p-6">
            <div className="flex justify-between mb-6">
              <div>
                <h2 className="text-2xl font-bold text-gray-800">{`Contrôles qualité #${entity.no}`}</h2>
                <Typography variant="body2" color="text.secondary">{getSubTitle()}</Typography>
              </div>
              <div className="flex items-center space-x-2">
                {hasContent && entity.qualityControlStatus === QualityControlStatus.IN_PROGRESS && (() => {
                  const entityLabel = entityType === 'preparationLot' ? 'la préparation' : 'l\'élution'
                  return (
                    <Tooltip title={canComplete ? `Passer ${entityLabel} en conforme` : `Les contrôles notés comme obligatoires doivent avoir été réalisés et être valides afin de passer ${entityLabel} en conforme`}>
                      <span style={{ cursor: 'pointer' }}>
                        <IconButton onClick={() => setCompleteOpen(true)} disabled={!canComplete || submitting} color="success">
                          <CheckCircleOutlineIcon />
                        </IconButton>
                      </span>
                    </Tooltip>
                  )
                })()}
                <Button variant="outlined" disabled={submitting} onClick={onClose}>
                  Fermer
                </Button>
              </div>
            </div>

            {completionLog && completedByLabel && (
              <Alert severity="success">
                Passée en Conforme par {completedByLabel} le {moment(completionLog.createdAt).format('DD/MM/YYYY à HH:mm')}
              </Alert>
            )}

            {loading && (
              <Box display="flex" justifyContent="center" py={4}>
                <CircularProgress size={32} />
              </Box>
            )}

            {!loading && !hasContent && (
              <Typography variant="body2" color="text.secondary" py={2} textAlign="center">
                Aucun contrôle qualité configuré pour ce composé.
              </Typography>
            )}

            {!loading && assignment && hasContent && (
              <Box display="flex" flexDirection="column" gap={2.5}>
                {assignment.visualQualityControls.length > 0 && (
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Caractères organoleptiques
                    </Typography>
                    <Box display="flex" flexDirection="column" gap={1.5}>
                      {assignment.visualQualityControls.map((vqc) => (
                        <ControlRow
                          key={vqc.id}
                          label={VisualQualityControlTypeLabels[vqc.type]}
                          expectedLabel={getQualityControlExpectedLabel({ kind: QCType.VISUAL, visualQualityControl: vqc })}
                          isRequired={vqc.type === VisualQualityControlType.COLOR ? assignment.colorIsRequired : assignment.appearanceIsRequired}
                          activeResult={getActiveResult((r) => r.visualQualityControl?.id === vqc.id)}
                          invalidatedResults={getInvalidatedResults((r) => r.visualQualityControl?.id === vqc.id)}
                          tooltipLabel={getRecordTooltip({ kind: QCType.VISUAL, visualQualityControl: vqc })}
                          onRecord={() => handleRecord({ kind: QCType.VISUAL, visualQualityControl: vqc, customQualityControl: null })}
                          onInvalidate={handleInvalidate}
                        />
                      ))}
                    </Box>
                  </Box>
                )}

                {(!isNil(assignment.radiochemicalPurityPercent) ||
                  !isNil(assignment.radionuclidicPurityPercent) ||
                  !isNil(assignment.phMinimum) ||
                  !isNil(assignment.phMaximum)) && (
                  <>
                    <Divider />
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        Spécifications analytiques
                      </Typography>
                      <Box display="flex" flexDirection="column" gap={1.5}>
                        {!isNil(assignment.radiochemicalPurityPercent) && (
                          <ControlRow
                            label="Pureté radiochimique"
                            expectedLabel={getQualityControlExpectedLabel({ kind: QCType.CHEMICAL_PURITY, assignment })}
                            isRequired={assignment.radiochemicalPurityIsRequired}
                            activeResult={getActiveResult((r) => r.type === QCType.CHEMICAL_PURITY)}
                            invalidatedResults={getInvalidatedResults((r) => r.type === QCType.CHEMICAL_PURITY)}
                            tooltipLabel={getRecordTooltip({ kind: QCType.CHEMICAL_PURITY, visualQualityControl: null })}
                            onRecord={() => handleRecord({ kind: QCType.CHEMICAL_PURITY, visualQualityControl: null, customQualityControl: null })}
                            onInvalidate={handleInvalidate}
                          />
                        )}
                        {!isNil(assignment.radionuclidicPurityPercent) && (
                          <ControlRow
                            label="Pureté radionuclidique"
                            expectedLabel={getQualityControlExpectedLabel({ kind: QCType.RADIONUCLIDIC_PURITY, assignment })}
                            isRequired={assignment.radionuclidicPurityIsRequired}
                            activeResult={getActiveResult((r) => r.type === QCType.RADIONUCLIDIC_PURITY)}
                            invalidatedResults={getInvalidatedResults((r) => r.type === QCType.RADIONUCLIDIC_PURITY)}
                            tooltipLabel={getRecordTooltip({ kind: QCType.RADIONUCLIDIC_PURITY, visualQualityControl: null })}
                            onRecord={() => handleRecord({ kind: QCType.RADIONUCLIDIC_PURITY, visualQualityControl: null, customQualityControl: null })}
                            onInvalidate={handleInvalidate}
                          />
                        )}
                        {(!isNil(assignment.phMinimum) || !isNil(assignment.phMaximum)) && (
                          <ControlRow
                            label="pH"
                            expectedLabel={getQualityControlExpectedLabel({ kind: QCType.PH, assignment })}
                            isRequired={assignment.phIsRequired}
                            activeResult={getActiveResult((r) => r.type === QCType.PH)}
                            invalidatedResults={getInvalidatedResults((r) => r.type === QCType.PH)}
                            tooltipLabel={getRecordTooltip({ kind: QCType.PH, visualQualityControl: null })}
                            onRecord={() => handleRecord({ kind: QCType.PH, visualQualityControl: null, customQualityControl: null })}
                            onInvalidate={handleInvalidate}
                          />
                        )}
                      </Box>
                    </Box>
                  </>
                )}

                {assignment.customQualityControls.length > 0 && (
                  <>
                    {assignment.visualQualityControls.length > 0 && <Divider />}
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        Contrôles personnalisés
                      </Typography>
                      <Box display="flex" flexDirection="column" gap={1.5}>
                        {assignment.customQualityControls.map((cqc) => (
                          <ControlRow
                            key={cqc.id}
                            label={cqc.label}
                            expectedLabel={getQualityControlExpectedLabel({ kind: QCType.CUSTOM, customQualityControl: cqc })}
                            isRequired={assignment.requiredCustomQualityControlIds.includes(cqc.id)}
                            activeResult={getActiveResult((r) => r.customQualityControl?.id === cqc.id)}
                            invalidatedResults={getInvalidatedResults((r) => r.customQualityControl?.id === cqc.id)}
                            tooltipLabel={getRecordTooltip({ kind: QCType.CUSTOM, customQualityControl: cqc })}
                            onRecord={() => handleRecord({ kind: QCType.CUSTOM, visualQualityControl: null, customQualityControl: cqc })}
                            onInvalidate={handleInvalidate}
                          />
                        ))}
                      </Box>
                    </Box>
                  </>
                )}
              </Box>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {assignment && recordTarget && (
        <RecordQcTestResultDialog
          open={!!recordTarget}
          onClose={() => setRecordTarget(null)}
          entityType={entityType}
          entityId={entity.id}
          kind={recordTarget.kind}
          visualQualityControl={recordTarget.visualQualityControl}
          customQualityControl={recordTarget.customQualityControl}
          assignment={assignment}
        />
      )}

      {invalidateTarget && (
        <InvalidateQcResultDialog
          open={!!invalidateTarget}
          onClose={() => setInvalidateTarget(null)}
          resultId={invalidateTarget.resultId}
          controlLabel={invalidateTarget.controlLabel}
          resultLabel={invalidateTarget.resultLabel}
          entityType={entityType}
          entityId={entity.id}
        />
      )}

      <CompleteQcDialog
        open={completeOpen}
        onClose={() => setCompleteOpen(false)}
        entityType={entityType}
        entityId={entity.id}
      />
    </>
  )
}
